import { formatCurrency } from './CurrencyFormat';

export type FrequencyPayment = {
  id?: string;
  paymentId?: string;
  debitId?: string;
  paymentType?: string;
  amount?: number;
  createdDate?: string;
  createdAt?: string;
  status?: string;
};

export type MonthlyFrequencyRow = {
  month: string;
  label: string;
  count: number;
  amount: number;
  interest: number;
  repayment: number;
  topUp: number;
  lateFee: number;
  other: number;
};

export type PaymentFrequencyStats = {
  totalCount: number;
  totalAmount: number;
  thisMonthCount: number;
  thisMonthAmount: number;
  lastPaymentDate: string | null;
  avgDaysBetween: number | null;
  medianDaysBetween: number | null;
};

function paymentDate(p: FrequencyPayment): Date | null {
  const raw = p.createdDate || p.createdAt;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function monthKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, (m || 1) - 1, 1);
  return d.toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
}

function typeBucket(paymentType?: string): keyof Pick<
  MonthlyFrequencyRow,
  'interest' | 'repayment' | 'topUp' | 'lateFee' | 'other'
> {
  const t = (paymentType || '').toUpperCase();
  if (t === 'INTEREST') return 'interest';
  if (t === 'REPAYMENT') return 'repayment';
  if (t === 'TOP_UP') return 'topUp';
  if (t === 'LATE_FEE') return 'lateFee';
  return 'other';
}

/** Exclude TOP_UP from cadence stats (funding events, not collections). */
export function isCollectionPayment(p: FrequencyPayment): boolean {
  const t = (p.paymentType || '').toUpperCase();
  return t !== 'TOP_UP';
}

export function buildMonthlyFrequency(payments: FrequencyPayment[]): MonthlyFrequencyRow[] {
  const map = new Map<string, MonthlyFrequencyRow>();
  for (const p of payments) {
    const d = paymentDate(p);
    if (!d) continue;
    const key = monthKey(d);
    let row = map.get(key);
    if (!row) {
      row = {
        month: key,
        label: monthLabel(key),
        count: 0,
        amount: 0,
        interest: 0,
        repayment: 0,
        topUp: 0,
        lateFee: 0,
        other: 0,
      };
      map.set(key, row);
    }
    const amt = Number(p.amount) || 0;
    row.count += 1;
    row.amount += amt;
    row[typeBucket(p.paymentType)] += amt;
  }
  return Array.from(map.values()).sort((a, b) => a.month.localeCompare(b.month));
}

export function computeFrequencyStats(payments: FrequencyPayment[]): PaymentFrequencyStats {
  const dated = payments
    .map((p) => ({ p, d: paymentDate(p) }))
    .filter((x): x is { p: FrequencyPayment; d: Date } => x.d != null)
    .sort((a, b) => a.d.getTime() - b.d.getTime());

  const collections = dated.filter(({ p }) => isCollectionPayment(p));
  const totalAmount = dated.reduce((s, { p }) => s + (Number(p.amount) || 0), 0);

  const now = new Date();
  const thisKey = monthKey(now);
  let thisMonthCount = 0;
  let thisMonthAmount = 0;
  for (const { p, d } of dated) {
    if (monthKey(d) === thisKey) {
      thisMonthCount += 1;
      thisMonthAmount += Number(p.amount) || 0;
    }
  }

  const gaps: number[] = [];
  for (let i = 1; i < collections.length; i++) {
    const days = (collections[i].d.getTime() - collections[i - 1].d.getTime()) / (1000 * 60 * 60 * 24);
    if (days >= 0) gaps.push(days);
  }
  const avgDaysBetween =
    gaps.length > 0 ? gaps.reduce((a, b) => a + b, 0) / gaps.length : null;
  let medianDaysBetween: number | null = null;
  if (gaps.length > 0) {
    const sorted = [...gaps].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    medianDaysBetween =
      sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  }

  const last = dated.length ? dated[dated.length - 1].d : null;

  return {
    totalCount: dated.length,
    totalAmount,
    thisMonthCount,
    thisMonthAmount,
    lastPaymentDate: last
      ? last.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
      : null,
    avgDaysBetween,
    medianDaysBetween,
  };
}

export function gapHistogram(payments: FrequencyPayment[]): { bucket: string; count: number }[] {
  const dated = payments
    .filter(isCollectionPayment)
    .map((p) => paymentDate(p))
    .filter((d): d is Date => d != null)
    .sort((a, b) => a.getTime() - b.getTime());

  const buckets = [
    { label: '≤7d', min: 0, max: 7 },
    { label: '8–14d', min: 8, max: 14 },
    { label: '15–30d', min: 15, max: 30 },
    { label: '31–45d', min: 31, max: 45 },
    { label: '46–60d', min: 46, max: 60 },
    { label: '>60d', min: 61, max: Infinity },
  ];
  const counts = buckets.map((b) => ({ bucket: b.label, count: 0 }));

  for (let i = 1; i < dated.length; i++) {
    const days = Math.round((dated[i].getTime() - dated[i - 1].getTime()) / (1000 * 60 * 60 * 24));
    const idx = buckets.findIndex((b) => days >= b.min && days <= b.max);
    if (idx >= 0) counts[idx].count += 1;
  }
  return counts;
}

export function formatDays(n: number | null): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `${n.toFixed(n < 10 ? 1 : 0)} days`;
}

export function formatAmountHint(amount: number): string {
  return formatCurrency(amount);
}

export type PeriodGrain = 'month' | 'week';

/** Continuous month/week series; empty periods use null so charts stay blank. */
export type PeriodFrequencyRow = {
  key: string;
  label: string;
  count: number | null;
  amount: number | null;
};

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Monday-start week key + label (ISO-ish local). */
function weekKeyAndLabel(d: Date): { key: string; label: string; weekStart: Date } {
  const day = startOfLocalDay(d);
  const dow = day.getDay(); // 0=Sun
  const offsetToMon = dow === 0 ? -6 : 1 - dow;
  const weekStart = new Date(day);
  weekStart.setDate(day.getDate() + offsetToMon);
  const y = weekStart.getFullYear();
  const m = String(weekStart.getMonth() + 1).padStart(2, '0');
  const dd = String(weekStart.getDate()).padStart(2, '0');
  const key = `${y}-${m}-${dd}`;
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6);
  const label = `${weekStart.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}–${weekEnd.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`;
  return { key, label, weekStart };
}

function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

function addWeeks(weekStart: Date, n: number): Date {
  const next = new Date(weekStart);
  next.setDate(weekStart.getDate() + n * 7);
  return next;
}

/**
 * Build a continuous month or week timeline covering [rangeStart, rangeEnd].
 * Periods with no payments get count/amount = null (blank on charts).
 */
export function buildPeriodSeries(
  payments: FrequencyPayment[],
  grain: PeriodGrain,
  options?: { rangeStart?: Date | string | null; rangeEnd?: Date | string | null }
): PeriodFrequencyRow[] {
  const dated = payments
    .map((p) => ({ p, d: paymentDate(p) }))
    .filter((x): x is { p: FrequencyPayment; d: Date } => x.d != null);

  const parseBound = (v?: Date | string | null): Date | null => {
    if (v == null || v === '') return null;
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const optStart = parseBound(options?.rangeStart ?? null);
  const optEnd = parseBound(options?.rangeEnd ?? null);
  const payTimes = dated.map(({ d }) => d.getTime());
  const minPay = payTimes.length ? Math.min(...payTimes) : null;
  const maxPay = payTimes.length ? Math.max(...payTimes) : null;

  const startMs = Math.min(
    ...[optStart?.getTime(), minPay].filter((t): t is number => t != null)
  );
  const endMs = Math.max(
    ...[optEnd?.getTime(), maxPay, Date.now()].filter((t): t is number => t != null)
  );

  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || startMs > endMs) {
    return [];
  }

  const totals = new Map<string, { count: number; amount: number; label: string }>();
  for (const { p, d } of dated) {
    let key: string;
    let label: string;
    if (grain === 'month') {
      key = monthKey(d);
      label = monthLabel(key);
    } else {
      const w = weekKeyAndLabel(d);
      key = w.key;
      label = w.label;
    }
    const cur = totals.get(key) ?? { count: 0, amount: 0, label };
    cur.count += 1;
    cur.amount += Number(p.amount) || 0;
    totals.set(key, cur);
  }

  const rows: PeriodFrequencyRow[] = [];
  if (grain === 'month') {
    let cursor = new Date(new Date(startMs).getFullYear(), new Date(startMs).getMonth(), 1);
    const end = new Date(new Date(endMs).getFullYear(), new Date(endMs).getMonth(), 1);
    while (cursor.getTime() <= end.getTime()) {
      const key = monthKey(cursor);
      const hit = totals.get(key);
      rows.push({
        key,
        label: monthLabel(key),
        count: hit ? hit.count : null,
        amount: hit ? hit.amount : null,
      });
      cursor = addMonths(cursor, 1);
    }
  } else {
    let cursor = weekKeyAndLabel(new Date(startMs)).weekStart;
    const endWeek = weekKeyAndLabel(new Date(endMs)).weekStart;
    while (cursor.getTime() <= endWeek.getTime()) {
      const { key, label } = weekKeyAndLabel(cursor);
      const hit = totals.get(key);
      rows.push({
        key,
        label,
        count: hit ? hit.count : null,
        amount: hit ? hit.amount : null,
      });
      cursor = addWeeks(cursor, 1);
    }
  }
  return rows;
}
